/**
 * Decibel arithmetic: sound levels, combining and subtracting levels, spreading with
 * distance, and power and voltage ratios.
 *
 * A decibel is ten times the common logarithm of a power ratio, so a ratio of
 * amplitudes (pressure, voltage) counts twice: 20 log₁₀. Sound levels use the standard
 * references I₀ = 10⁻¹² W/m² and p₀ = 20 µPa in air.
 */

/** Reference intensity for sound intensity level, W/m² (threshold of hearing at 1 kHz). */
export const REFERENCE_INTENSITY = 1e-12;
/** Reference sound pressure in air, Pa. */
export const REFERENCE_PRESSURE = 20e-6;
/** The 0 dBu reference voltage: √(1 mW × 600 Ω) = 0.7746 V RMS. */
export const DBU_REFERENCE = Math.sqrt(0.6);

const log10 = Math.log10;

export function levelFromIntensity(intensity: number): number {
  return 10 * log10(intensity / REFERENCE_INTENSITY);
}

export function intensityFromLevel(level: number): number {
  return REFERENCE_INTENSITY * 10 ** (level / 10);
}

export function levelFromPressure(pressure: number): number {
  return 20 * log10(pressure / REFERENCE_PRESSURE);
}

export function pressureFromLevel(level: number): number {
  return REFERENCE_PRESSURE * 10 ** (level / 20);
}

/** Total of incoherent sources: 10 log₁₀ Σ 10^(Lᵢ/10). Null for an empty list. */
export function addLevels(levels: readonly number[]): number | null {
  if (levels.length === 0) {
    return null;
  }
  return 10 * log10(levels.reduce((sum, level) => sum + 10 ** (level / 10), 0));
}

/**
 * The level of one source once a background is taken out of a measured total. Null when
 * the background is as loud as the total or louder: nothing is left to measure.
 */
export function subtractLevel(
  total: number,
  background: number
): number | null {
  const remaining = 10 ** (total / 10) - 10 ** (background / 10);
  return remaining > 0 ? 10 * log10(remaining) : null;
}

/** `count` identical sources raise the level by 10 log₁₀ count. */
export function identicalSources(level: number, count: number): number {
  return level + 10 * log10(count);
}

/** Level at distance r₂ from a point source measured at r₁ in a free field. */
export function levelAtDistance(
  level1: number,
  r1: number,
  r2: number
): number {
  return level1 - 20 * log10(r2 / r1);
}

/** Distance at which a point source's level falls to `level2`. */
export function distanceForLevel(
  level1: number,
  r1: number,
  level2: number
): number {
  return r1 * 10 ** ((level1 - level2) / 20);
}

/** The drop each time the distance doubles for a point source: 20 log₁₀ 2 ≈ 6.02 dB. */
export const DB_PER_DOUBLING = 20 * log10(2);

export type RatioKind = "power" | "amplitude";

export function ratioToDb(ratio: number, kind: RatioKind): number {
  return (kind === "power" ? 10 : 20) * log10(ratio);
}

export function dbToRatio(db: number, kind: RatioKind): number {
  return 10 ** (db / (kind === "power" ? 10 : 20));
}

/** Power in watts to dBm (relative to 1 mW). */
export function wattsToDbm(watts: number): number {
  return 10 * log10(watts / 1e-3);
}

export function dbmToWatts(dbm: number): number {
  return 1e-3 * 10 ** (dbm / 10);
}

export function wattsToDbw(watts: number): number {
  return 10 * log10(watts);
}

export function voltsToDbv(volts: number): number {
  return 20 * log10(volts);
}

export function voltsToDbu(volts: number): number {
  return 20 * log10(volts / DBU_REFERENCE);
}

export function dbvToVolts(dbv: number): number {
  return 10 ** (dbv / 20);
}

export function dbuToVolts(dbu: number): number {
  return DBU_REFERENCE * 10 ** (dbu / 20);
}
